import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFetch } from './api';
import SettingsHeader from './SettingsHeader';
import DeleteObjectModal from './DeleteObjectModal';
import GenerateMagicLinkModal from './GenerateMagicLinkModal';

function MagicLinks() {
  const { connectionData, integrationLists, integrationListsLoading } = useOutletContext();
  const { organization } = useOrganization();
  const [loadingMagicLinks, setLoadingMagicLinks] = useState(false);
  const [magicLinks, setMagicLinks] = useState([]);
  const [isDeletingMagicLink, setIsDeletingMagicLink] = useState(false);
  const [magicLinkCopied, setMagicLinkCopied] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [generateMagicLinkModalOpen, setGenerateMagicLinkModalOpen] = useState(false);

  const trackstar = useFetch();

  const copyToClipboard = (content) => {
    const textArea = document.createElement('textarea');
    textArea.value = content;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    setMagicLinkCopied(content);
    setTimeout(() => {
      setMagicLinkCopied('');
    }, 1500); // Reset the copied state after 1.5 seconds
  };

  useEffect(() => {
    if (organization) {
      setLoadingMagicLinks(true);
      trackstar('internal/link/magic-link').then((response) => {
        const data = response['magic-links'];
        setMagicLinks(data);
        setLoadingMagicLinks(false);
      });
    }
  }, []);

  const handleCloseDeleteModal = (cancelled) => {
    if (!cancelled) {
      setShowDeleteConfirmation(true);
    }
    setDeleteModalOpen(false);
    setRowToDelete(null);
  };

  const handleDeleteMagicLink = (magicLink) => {
    setIsDeletingMagicLink(true);
    trackstar(`internal/link/magic-link/${magicLink}`, 'DELETE').then(() => {
      setMagicLinks(
        (
          prevMagicLinks,
        ) => prevMagicLinks.filter((prevMagicLink) => prevMagicLink.id !== magicLink),
      );
      handleCloseDeleteModal(false);
      setIsDeletingMagicLink(false);
    });
  };

  const handleOpenDeleteModal = (row) => {
    setDeleteModalOpen(true);
    setRowToDelete(row);
  };

  const handleGenerateMagicLinkModalClose = (returnedMagicLink) => {
    setGenerateMagicLinkModalOpen(false);
    if (returnedMagicLink) {
      setLoadingMagicLinks(true);
      trackstar('internal/link/magic-link').then((response) => {
        const data = response['magic-links'];
        setMagicLinks(data);
        setLoadingMagicLinks(false);
      });
      setShowCreateConfirmation(true);
    }
  };

  function noMagicLinks() {
    return (
      <div className="no-rows-overlay">
        <div className="no-rows-overlay-icon">
          <svg className="w-6 h-6 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
        <p className="no-rows-overlay-title">No Magic Links</p>
        <p className="no-rows-overlay-text">Create a magic link to enable passwordless authentication for your users.</p>
        <button
          type="button"
          className="btn btn-accent"
          onClick={() => setGenerateMagicLinkModalOpen(true)}
        >
          Generate Magic Link
        </button>
      </div>
    );
  }

  const columns = [{
    field: 'id',
    headerName: 'ID',
    flex: 2,
    renderCell: (params) => (
      <div className="flex items-center h-full w-full justify-between gap-3">
        <span className="font-mono text-xs text-surface-600 truncate">{params.value}</span>
        <button
          type="button"
          onClick={() => copyToClipboard(`https://link.trackstarhq.com/${params.row.id}`)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-150 flex-shrink-0 ${
            magicLinkCopied === `https://link.trackstarhq.com/${params.row.id}`
              ? 'bg-success-light text-success-dark'
              : 'bg-surface-900 text-white hover:bg-surface-800'
          }`}
        >
          {magicLinkCopied === `https://link.trackstarhq.com/${params.row.id}` ? 'Copied!' : 'Copy URL'}
        </button>
      </div>
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    flex: 1,
    renderCell: (params) => (
      <span>{new Date(params.value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
    ),
  },
  {
    field: 'expires_at',
    headerName: 'Expires At',
    flex: 1,
    renderCell: (params) => (
      // params.value is a utc epoch timestamp
      <span>{new Date(params.value * 1000).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
    ),
  },
  {
    field: 'customer_id',
    headerName: 'Customer ID',
    flex: 1,
    renderCell: (params) => (
      <span>{params.value}</span>
    ),
  },
  {
    field: 'integration_allow_list',
    headerName: 'Integration Allow List',
    flex: 1,
    renderCell: (params) => (
      <span>{params.value.join(', ')}</span>
    ),
  },
  {
    field: 'delete',
    width: 50,
    headerName: '',
    disableColumnMenu: true,
    filterable: false,
    resizable: false,
    sortable: false,
    headerClassName: 'column-header-no-border',
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={() => handleOpenDeleteModal(params.row.id)}
        className="text-surface-400 hover:text-error-dark hover:bg-error-light"
        sx={{ padding: '6px' }}
      >
        <DeleteOutlineIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    ),
  }];

  return (
    <div className="flex flex-col gap-5">
      <SettingsHeader
        title="Magic Links"
        subtitle="Generate one-time connection links"
        actions={(
          <button
            type="button"
            className="btn btn-accent"
            onClick={() => setGenerateMagicLinkModalOpen(true)}
          >
            Generate Magic Link
          </button>
        )}
      />
      {showCreateConfirmation && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-success-light border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-success" />
          <p className="text-sm font-medium text-success-dark">Magic Link successfully created!</p>
        </div>
      )}
      {showDeleteConfirmation && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-success-light border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-success" />
          <p className="text-sm font-medium text-success-dark">Magic Link successfully deleted!</p>
        </div>
      )}
      <div className="card overflow-hidden">
        <DataGridPro
          rows={magicLinks}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[]}
          loading={loadingMagicLinks}
          pagination
          autoHeight
          slots={{ noRowsOverlay: noMagicLinks }}
          sortModel={[{ field: 'created_at', sort: 'desc' }]}
          rowHeight={48}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#FAFAF9',
              borderBottom: '1px solid #E7E5E4',
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontWeight: 500,
              fontSize: '0.8125rem',
              color: '#78716C',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.8125rem',
              fontWeight: 400,
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              color: '#44403C',
              borderBottom: '1px solid #F5F5F4',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FAFAF9',
            },
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        />
      </div>
      <DeleteObjectModal
        isOpen={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteMagicLink}
        isLoading={isDeletingMagicLink}
        row={rowToDelete}
        objectType="Magic Link"
      />
      <GenerateMagicLinkModal
        isOpen={generateMagicLinkModalOpen}
        onClose={handleGenerateMagicLinkModalClose}
        connectionRows={connectionData}
        integrationLists={integrationLists}
        integrationListsLoading={integrationListsLoading}
      />
    </div>
  );
}

export default MagicLinks;
